import { useEffect, useState, type FormEvent } from 'react'
import { Loader2 } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { api, ApiError } from '../../lib/api'
import { confirmDialog } from '../../lib/dialogs'
import type { Firm } from '@jr2os/shared'
import { useAuth } from '../../contexts/AuthContext'

export default function FirmPage() {
  const { t } = useTranslation('settings')
  const auth = useAuth()
  const canEdit = auth.status === 'authenticated' && auth.user.role === 'ADMIN'

  const [firm, setFirm] = useState<Firm | null>(null)
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [message, setMessage] = useState<{ kind: 'ok' | 'error'; text: string } | null>(null)

  // Logo del PDF de facturas/presupuestos. logoVersion fuerza recarga del <img>
  // tras subir/borrar; logoMissing = GET /firm/logo devolvió 404 (usa el estático).
  const [logoVersion, setLogoVersion] = useState(1)
  const [logoMissing, setLogoMissing] = useState(false)
  const [logoBusy, setLogoBusy] = useState(false)

  const onUploadLogo = async (file: File | null) => {
    if (!file) return
    setLogoBusy(true)
    setMessage(null)
    try {
      const form = new FormData()
      form.append('file', file)
      await api.upload('/firm/logo', form)
      setLogoMissing(false)
      setLogoVersion((v) => v + 1)
      setMessage({ kind: 'ok', text: t('firm.logo.uploaded') })
    } catch (err) {
      const body = err instanceof ApiError ? (err.body as { error?: string }) : undefined
      setMessage({ kind: 'error', text: body?.error ?? (err instanceof Error ? err.message : 'Error') })
    } finally {
      setLogoBusy(false)
    }
  }

  const onRemoveLogo = async () => {
    if (!(await confirmDialog({ message: t('firm.logo.confirmRemove'), tone: 'danger' }))) return
    setLogoBusy(true)
    try {
      await api.delete('/firm/logo')
      setLogoMissing(true)
      setLogoVersion((v) => v + 1)
    } catch (err) {
      const body = err instanceof ApiError ? (err.body as { error?: string }) : undefined
      setMessage({ kind: 'error', text: body?.error ?? (err instanceof Error ? err.message : 'Error') })
    } finally {
      setLogoBusy(false)
    }
  }

  useEffect(() => {
    void (async () => {
      try {
        const { firm } = await api.get<{ firm: Firm }>('/firm')
        setFirm(firm)
      } catch (err) {
        setMessage({ kind: 'error', text: err instanceof Error ? err.message : t('firm.errorLoad') })
      } finally {
        setLoading(false)
      }
    })()
  }, [])

  if (loading) {
    return (
      <div className="t-muted flex items-center gap-2">
        <Loader2 size={18} className="animate-spin" /> {t('firm.loading')}
      </div>
    )
  }
  if (!firm) return <div className="t-muted">{t('firm.notAvailable')}</div>

  const update = (patch: Partial<Firm>) => setFirm({ ...firm, ...patch })

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault()
    setSaving(true)
    setMessage(null)
    try {
      const { firm: updated } = await api.patch<{ firm: Firm }>('/firm', {
        name: firm.name,
        legalName: firm.legalName,
        cif: firm.cif,
        email: firm.email,
        phone: firm.phone,
        address: firm.address,
        city: firm.city,
        postalCode: firm.postalCode,
        province: firm.province,
        country: firm.country,
        vatRate: firm.vatRate,
        irpfRate: firm.irpfRate,
        invoicePrefix: firm.invoicePrefix,
        invoiceFootnote: firm.invoiceFootnote,
        bankIban: firm.bankIban,
        bankBic: firm.bankBic,
        defaultHourlyRateCents: firm.defaultHourlyRateCents,
        mileageRateCents: firm.mileageRateCents,
        perDiemNationalCents: firm.perDiemNationalCents,
        perDiemNationalOvernightCents: firm.perDiemNationalOvernightCents,
        perDiemAbroadCents: firm.perDiemAbroadCents,
        perDiemAbroadOvernightCents: firm.perDiemAbroadOvernightCents,
        defaultCurrency: firm.defaultCurrency,
        defaultLanguage: firm.defaultLanguage,
        ledgerLockedUntil: firm.ledgerLockedUntil,
        enforceMfaRoles: firm.enforceMfaRoles,
        sessionIdleMinutes: firm.sessionIdleMinutes,
        sessionAbsoluteHours: firm.sessionAbsoluteHours,
      })
      setFirm(updated)
      setMessage({ kind: 'ok', text: t('firm.saveOk') })
    } catch (err) {
      const text =
        err instanceof ApiError
          ? ((err.body as { error?: string })?.error ?? err.message)
          : t('firm.errorNetwork')
      setMessage({ kind: 'error', text })
    } finally {
      setSaving(false)
    }
  }

  const inputCls =
    'w-full px-3 py-2 rounded-lg bg-[var(--bg-input)] border border-[var(--border)] t-primary focus:outline-none focus:ring-1 focus:ring-jr2-500/40 focus:border-jr2-500/60 disabled:opacity-60 disabled:cursor-not-allowed'

  return (
    <div className="space-y-6">
      <header>
        <h2 className="text-2xl font-serif font-semibold t-primary">{t('firm.title')}</h2>
        <p className="t-muted text-sm mt-1">
          {canEdit
            ? t('firm.subtitleEdit')
            : t('firm.subtitleReadonly')}
        </p>
      </header>

      <form onSubmit={onSubmit} className="surface p-6 space-y-6">
        <fieldset disabled={!canEdit || saving} className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Field label={t('firm.fields.name')}>
              <input
                required
                value={firm.name}
                onChange={(e) => update({ name: e.target.value })}
                className={inputCls}
              />
            </Field>
            <Field label={t('firm.fields.legalName')}>
              <input
                value={firm.legalName ?? ''}
                onChange={(e) => update({ legalName: e.target.value })}
                className={inputCls}
              />
            </Field>
            <Field label={t('firm.fields.cif')}>
              <input
                value={firm.cif ?? ''}
                onChange={(e) => update({ cif: e.target.value })}
                className={inputCls}
              />
            </Field>
            <Field label={t('firm.fields.email')}>
              <input
                type="email"
                value={firm.email ?? ''}
                onChange={(e) => update({ email: e.target.value })}
                className={inputCls}
              />
            </Field>
            <Field label={t('firm.fields.phone')}>
              <input
                value={firm.phone ?? ''}
                onChange={(e) => update({ phone: e.target.value })}
                className={inputCls}
              />
            </Field>
            <Field label={t('firm.fields.country')}>
              <input
                value={firm.country}
                onChange={(e) => update({ country: e.target.value.toUpperCase() })}
                maxLength={2}
                className={inputCls}
              />
            </Field>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Field label={t('firm.fields.address')}>
              <input
                value={firm.address ?? ''}
                onChange={(e) => update({ address: e.target.value })}
                className={inputCls}
              />
            </Field>
            <Field label={t('firm.fields.city')}>
              <input
                value={firm.city ?? ''}
                onChange={(e) => update({ city: e.target.value })}
                className={inputCls}
              />
            </Field>
            <Field label={t('firm.fields.postalCode')}>
              <input
                value={firm.postalCode ?? ''}
                onChange={(e) => update({ postalCode: e.target.value })}
                className={inputCls}
              />
            </Field>
            <Field label={t('firm.fields.province')}>
              <input
                value={firm.province ?? ''}
                onChange={(e) => update({ province: e.target.value })}
                className={inputCls}
              />
            </Field>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 border-t border-base pt-4">
            <Field label={t('firm.fields.vatRate')}>
              <input
                type="number"
                step="0.01"
                min={0}
                max={100}
                value={firm.vatRate}
                onChange={(e) => update({ vatRate: Number(e.target.value) })}
                className={inputCls}
              />
            </Field>
            <Field label={t('firm.fields.irpfRate')}>
              <input
                type="number"
                step="0.01"
                min={0}
                max={100}
                value={firm.irpfRate}
                onChange={(e) => update({ irpfRate: Number(e.target.value) })}
                className={inputCls}
              />
            </Field>
            <Field label={t('firm.fields.invoicePrefix')}>
              <input
                value={firm.invoicePrefix}
                onChange={(e) => update({ invoicePrefix: e.target.value })}
                className={inputCls}
              />
            </Field>
            <Field label={t('firm.fields.hourlyRate')}>
              <input
                type="number"
                step="0.01"
                min={0}
                value={(firm.defaultHourlyRateCents / 100).toFixed(2)}
                onChange={(e) =>
                  update({
                    defaultHourlyRateCents: Math.round(Number(e.target.value || '0') * 100),
                  })
                }
                placeholder="0"
                className={`${inputCls} font-mono`}
              />
            </Field>
          </div>

          {/* Tarifas de la hoja de gastos. Los LÍMITES exentos los fija Hacienda
              y no se configuran: estos campos no los pueden superar, porque el
              exceso es salario sujeto a retención y cotización y se liquida en
              nómina, no aquí. */}
          <div className="border-t border-base pt-4">
            <h3 className="text-sm font-semibold t-primary">{t('firm.allowances.title')}</h3>
            <p className="text-xs t-muted mt-0.5 mb-3">{t('firm.allowances.help')}</p>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              <Field label={t('firm.allowances.mileage')}>
                <input
                  type="number"
                  step="0.01"
                  min={0}
                  max={0.26}
                  value={(firm.mileageRateCents / 100).toFixed(2)}
                  onChange={(e) =>
                    update({ mileageRateCents: Math.round(Number(e.target.value || '0') * 100) })
                  }
                  className={`${inputCls} font-mono`}
                />
              </Field>
              <Field label={t('firm.allowances.national')}>
                <input
                  type="number"
                  step="0.01"
                  min={0}
                  max={26.67}
                  value={(firm.perDiemNationalCents / 100).toFixed(2)}
                  onChange={(e) =>
                    update({ perDiemNationalCents: Math.round(Number(e.target.value || '0') * 100) })
                  }
                  className={`${inputCls} font-mono`}
                />
              </Field>
              <Field label={t('firm.allowances.nationalOvernight')}>
                <input
                  type="number"
                  step="0.01"
                  min={0}
                  max={53.34}
                  value={(firm.perDiemNationalOvernightCents / 100).toFixed(2)}
                  onChange={(e) =>
                    update({
                      perDiemNationalOvernightCents: Math.round(Number(e.target.value || '0') * 100),
                    })
                  }
                  className={`${inputCls} font-mono`}
                />
              </Field>
              <Field label={t('firm.allowances.abroad')}>
                <input
                  type="number"
                  step="0.01"
                  min={0}
                  max={48.08}
                  value={(firm.perDiemAbroadCents / 100).toFixed(2)}
                  onChange={(e) =>
                    update({ perDiemAbroadCents: Math.round(Number(e.target.value || '0') * 100) })
                  }
                  className={`${inputCls} font-mono`}
                />
              </Field>
              <Field label={t('firm.allowances.abroadOvernight')}>
                <input
                  type="number"
                  step="0.01"
                  min={0}
                  max={91.35}
                  value={(firm.perDiemAbroadOvernightCents / 100).toFixed(2)}
                  onChange={(e) =>
                    update({
                      perDiemAbroadOvernightCents: Math.round(Number(e.target.value || '0') * 100),
                    })
                  }
                  className={`${inputCls} font-mono`}
                />
              </Field>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 border-t border-base pt-4">
            <Field label={t('firm.fields.defaultCurrency')}>
              <input
                value={firm.defaultCurrency}
                onChange={(e) => update({ defaultCurrency: e.target.value.toUpperCase() })}
                maxLength={3}
                placeholder="EUR"
                className={`${inputCls} font-mono uppercase`}
              />
            </Field>
            <Field label={t('firm.fields.defaultLanguage')}>
              <input
                value={firm.defaultLanguage}
                onChange={(e) => update({ defaultLanguage: e.target.value.toLowerCase() })}
                maxLength={2}
                placeholder="es"
                className={`${inputCls} font-mono lowercase`}
              />
            </Field>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 border-t border-base pt-4">
            <Field label={t('firm.fields.iban')}>
              <input
                value={firm.bankIban ?? ''}
                onChange={(e) => update({ bankIban: e.target.value })}
                placeholder="ES00 0000 0000 00 0000000000"
                className={`${inputCls} font-mono text-sm`}
              />
            </Field>
            <Field label={t('firm.fields.bic')}>
              <input
                value={firm.bankBic ?? ''}
                onChange={(e) => update({ bankBic: e.target.value })}
                placeholder="BBVAESMM"
                className={`${inputCls} font-mono text-sm`}
              />
            </Field>
            <div className="md:col-span-2">
              <Field label={t('firm.fields.invoiceFootnote')}>
                <textarea
                  value={firm.invoiceFootnote ?? ''}
                  onChange={(e) => update({ invoiceFootnote: e.target.value })}
                  rows={3}
                  placeholder={t('firm.invoiceFootnotePlaceholder')}
                  className={inputCls}
                />
              </Field>
            </div>
            {/* F1 — Bloqueo de periodos contables (fecha tope estilo ContaPlus). */}
            <div className="md:col-span-2">
              <Field label={t('firm.fields.ledgerLockedUntil')}>
                <div className="flex flex-wrap items-center gap-2">
                  <input
                    type="date"
                    value={firm.ledgerLockedUntil ?? ''}
                    onChange={(e) => update({ ledgerLockedUntil: e.target.value || null })}
                    className={`${inputCls} w-auto`}
                  />
                  {firm.ledgerLockedUntil && (
                    <button
                      type="button"
                      onClick={() => update({ ledgerLockedUntil: null })}
                      className="px-2.5 py-1.5 rounded-lg text-xs border border-base hover:bg-hover t-secondary"
                    >
                      {t('firm.ledgerLockClear')}
                    </button>
                  )}
                </div>
                <p className="text-xs t-muted mt-1">{t('firm.ledgerLockedUntilHint')}</p>
              </Field>
            </div>
            <div className="md:col-span-2">
              <Field label={t('firm.logo.label')}>
                <div className="flex items-center gap-4 flex-wrap">
                  {/* GET /firm/logo devuelve 404 si no hay logo subido → se usa el estático. */}
                  {logoVersion > 0 && !logoMissing ? (
                    <img
                      src={`/api/firm/logo?v=${logoVersion}`}
                      alt="logo"
                      onError={() => setLogoMissing(true)}
                      className="h-12 max-w-[220px] object-contain rounded border border-base bg-white p-1"
                    />
                  ) : (
                    <span className="text-xs t-faint">{t('firm.logo.usingDefault')}</span>
                  )}
                  {canEdit && (
                    <div className="flex items-center gap-2">
                      <label className="px-3 py-1.5 rounded-lg text-xs t-secondary border border-base hover:bg-hover cursor-pointer">
                        {logoBusy ? t('firm.logo.uploading') : t('firm.logo.upload')}
                        <input
                          type="file"
                          accept="image/png,image/jpeg"
                          className="hidden"
                          disabled={logoBusy}
                          onChange={(e) => void onUploadLogo(e.target.files?.[0] ?? null)}
                        />
                      </label>
                      {!logoMissing && logoVersion > 0 && (
                        <button
                          type="button"
                          disabled={logoBusy}
                          onClick={() => void onRemoveLogo()}
                          className="px-3 py-1.5 rounded-lg text-xs text-rose-700 dark:text-rose-300 border border-rose-500/30 hover:bg-rose-500/10"
                        >
                          {t('firm.logo.remove')}
                        </button>
                      )}
                    </div>
                  )}
                </div>
                <p className="text-[11px] t-faint mt-1">{t('firm.logo.hint')}</p>
              </Field>
            </div>
          </div>
        </fieldset>

        {/* F15.3 — Políticas de seguridad de la compañía */}
        <fieldset disabled={!canEdit} className="space-y-4 border-t border-base pt-5">
          <legend className="text-sm font-semibold t-primary mb-2">
            {t('firm.security.legend')}
          </legend>
          <p className="text-xs t-muted -mt-1">
            {t('firm.security.note')}
          </p>

          <div>
            <span className="block text-xs t-muted mb-2">
              {t('firm.security.mfaPrompt')}
            </span>
            <div className="flex flex-wrap gap-2">
              {(['ADMIN', 'CONSULTOR', 'ANALISTA', 'LECTURA', 'CONTABILIDAD'] as const).map((r) => {
                const checked = firm.enforceMfaRoles.includes(r)
                return (
                  <label
                    key={r}
                    className={[
                      'inline-flex items-center gap-2 px-3 py-1.5 rounded-lg border text-xs cursor-pointer transition',
                      checked
                        ? 'bg-jr2-500/15 text-jr2-700 dark:text-jr2-200 border-jr2-500/40'
                        : 'bg-[var(--bg-input-soft)] t-secondary border-[var(--border)] hover:bg-[var(--bg-input)]',
                    ].join(' ')}
                  >
                    <input
                      type="checkbox"
                      checked={checked}
                      onChange={(e) => {
                        const next = e.target.checked
                          ? [...firm.enforceMfaRoles, r]
                          : firm.enforceMfaRoles.filter((x) => x !== r)
                        update({ enforceMfaRoles: next })
                      }}
                      className="sr-only"
                    />
                    {r}
                  </label>
                )
              })}
            </div>
            <p className="text-[11px] t-faint mt-1.5">
              {t('firm.security.mfaHint')}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Field label={t('firm.fields.sessionIdleMinutes')}>
              <input
                type="number"
                min={5}
                max={720}
                value={firm.sessionIdleMinutes}
                onChange={(e) =>
                  update({ sessionIdleMinutes: Number(e.target.value || '30') })
                }
                className={`${inputCls} font-mono`}
              />
            </Field>
            <Field label={t('firm.fields.sessionAbsoluteHours')}>
              <input
                type="number"
                min={1}
                max={168}
                value={firm.sessionAbsoluteHours}
                onChange={(e) =>
                  update({ sessionAbsoluteHours: Number(e.target.value || '8') })
                }
                className={`${inputCls} font-mono`}
              />
            </Field>
          </div>
        </fieldset>

        {message && (
          <div
            className={[
              'text-sm rounded-lg px-3 py-2 border',
              message.kind === 'ok'
                ? 'text-emerald-700 dark:text-emerald-300 bg-emerald-500/10 border-emerald-500/40'
                : 'text-rose-700 dark:text-rose-300 bg-rose-500/10 border-rose-500/30',
            ].join(' ')}
          >
            {message.text}
          </div>
        )}

        {canEdit && (
          <div className="flex justify-end">
            <button
              type="submit"
              disabled={saving}
              className="px-4 py-2 rounded-lg text-sm font-semibold text-white bg-jr2-600 hover:bg-jr2-700 hover:shadow-glow-jr2 disabled:opacity-50 flex items-center gap-2"
            >
              {saving && <Loader2 size={14} className="animate-spin" />}
              {t('firm.save')}
            </button>
          </div>
        )}
      </form>
    </div>
  )
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <label className="block text-sm t-secondary mb-1.5">{label}</label>
      {children}
    </div>
  )
}
